import { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, message, Select } from 'antd';
import { Spin } from 'antd';
import api from '@/api/dashboard';
import publicMethod, { disabledDate } from '@/utils/publicMethod';
import Chart from '@/components/Chart';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import updateLocale from 'dayjs/plugin/updateLocale';
import { calendarIcon } from '@/constants/static';
import format from '@/utils/format';
import useDownLoadCSV from '@/hook/useDownLoadCSV';
import businessAdminUserApi from '@/api/businessAdminUser';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
    weekStart: 1, // 设置周一为一周的第一天 - Set Monday as the first day of the week
});
export interface ChartData {
    xdata: string[];
    ydata: [{ name: string; data: string[] }];
}

const Dashboard = () => {
    const [chartData, setChartData] = useState<{
        totalMarketValue: ChartData | null;
        onlineUser: ChartData | null;
        newUser: ChartData | null;
        totalUser: ChartData | null;
    }>({
        totalMarketValue: null,
        onlineUser: null,
        newUser: null,
        totalUser: null,
    });
    const [loading, setLoading] = useState<{
        totalMarketValue: boolean;
        onlineUser: boolean;
        newUser: boolean;
        totalUser: boolean;
        page: boolean;
    }>({
        totalMarketValue: false,
        onlineUser: false,
        newUser: false,
        totalUser: false,
        page: false,
    });

    const [state, setState] = useState<any>({
        allTasksEarnedToday: '0.00',
        faastaaFees: '0.00',
        nowOfActiveCustomersOnline: '0',
        nowOfActiveTaskersOnline: '0',
        taskerFees: '0.00',
        tasksConfirmedToday: '0',
        totalEarnings: '0.00',
        totalOfCustomers: '0',
        totalOfTaskers: '0',
        totalOfBusinessAccounts: '0',
    });

    const changeLoading = (key: string, val: boolean) => {
        setLoading(pre => ({
            ...pre,
            [key]: val,
        }));
    };
    const getInfo = async () => {
        changeLoading('page', true);
        try {
            const res = await api.getDashboardInfo(currentSelect);
            setState((pre: any) => ({
                ...pre,
                ...res.data,
                totalOfBusinessAccounts: res?.data?.totalOfBusinessAccounts ?? pre.totalOfBusinessAccounts ?? '0',
            }));
        } catch (error: any) {
            message.error(error);
        } finally {
            changeLoading('page', false);
        }
    };
    const getTotalMarketValue = async () => {
        changeLoading('totalMarketValue', true);
        try {
            const res = await api.getDashboardTotalMarketValue(currentSelect);
            const { xdata, ydata } = res.data;
            setChartData(pre => ({
                ...pre,
                totalMarketValue: xdata.length && ydata.length ? res.data : null,
            }));
        } catch (error: any) {
            message.error(error);
        } finally {
            changeLoading('totalMarketValue', false);
        }
    };
    const getOnlineUser = async () => {
        changeLoading('onlineUser', true);
        try {
            const res = await api.getDashboardOnlineUders(currentSelect);
            const { xdata, ydata } = res.data;
            setChartData(pre => ({
                ...pre,
                onlineUser: xdata.length && ydata.length ? res.data : null,
            }));
        } catch (error: any) {
            message.error(error);
        } finally {
            changeLoading('onlineUser', false);
        }
    };

    const getNewUser = async () => {
        changeLoading('newUser', true);
        try {
            const res = await api.getDashboardNewUsers(currentSelect);
            const { xdata, ydata } = res.data;
            setChartData(pre => ({
                ...pre,
                newUser: xdata.length && ydata.length ? res.data : null,
            }));
        } catch (error: any) {
            console.log(error, 'error');
            message.error(error);
        } finally {
            changeLoading('newUser', false);
        }
    };

    const getTotalUser = async () => {
        changeLoading('totalUser', true);
        try {
            const res = await api.getDashboardTotalUsers(currentSelect);
            const { xdata, ydata } = res.data;
            setChartData(pre => ({
                ...pre,
                totalUser: xdata.length && ydata.length ? res.data : null,
            }));
        } catch (error: any) {
            message.error(error);
        } finally {
            changeLoading('totalUser', false);
        }
    };
    const getBusinessAdminUsersCount = async () => {
        try {
            const res = await businessAdminUserApi.getBusinessAdminUserList({ pageNum: 1, pageSize: 1, keyword: '' });
            const total =
                res?.data?.total ??
                res?.data?.metaData?.total ??
                (Array.isArray(res?.data?.records) ? res.data.records.length : 0);
            setState(pre => ({
                ...pre,
                totalOfBusinessAccounts: window.utils.formatNum(total || 0),
            }));
        } catch (error: any) {
            message.error(error);
        }
    };
    // v1.1.0
    const filterTypes = [
        {
            label: 'All Data',
            value: 'all',
        },
        {
            label: 'Today',
            value: 'today',
        },
        {
            label: 'Yesterday',
            value: 'yesterday',
        },
        {
            label: 'Week to date',
            value: 'week',
        },
        {
            label: 'Month to date',
            value: 'month',
        },
        {
            label: 'Financial year to date',
            value: 'financial',
        },
        {
            label: 'Prev fin year',
            value: 'prev',
        },
        {
            label: 'Custom date',
            value: 'custom',
        },
    ];
    const [currentSelect, setSelectValue] = useState({
        dateType: 'all',
        startTime: '',
        endTime: '',
    });
    useEffect(() => {
        getInfo();
        getTotalMarketValue();
        getOnlineUser();
        getNewUser();
        getTotalUser();
        getBusinessAdminUsersCount();
    }, [currentSelect]);
    const { RangePicker } = DatePicker;
    const [rangePickerVisible, setRangePickerVisible] = useState(false);
    /**
     * 根据筛选选项返回日期范围（格式化为 DD/MM/YYYY）
     * @param {string | number} filterValue - 筛选选项（today, yesterday, ...）
     * @returns {object} { startDate, endDate }（格式化为 DD/MM/YYYY）
     */
    const getDateRangeByFilter = (
        filterValue: string | number,
        end?: string
    ): { startDate: string; endDate: string } => {
        if (end) {
            return {
                startDate: dayjs(filterValue).format('DD/MM/YYYY'),
                endDate: dayjs(end).format('DD/MM/YYYY'),
            };
        }
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        // 澳洲财政年度：7月1日 ~ 次年6月30日
        const currentYear = today.year();
        let financialYearStart;
        // 上一个完整财政年度（2023年7月1日 ~ 2024年6月30日）
        const prevFinancialYearStart = dayjs(`${today.year() - 2}-07-01`);
        const prevFinancialYearEnd = dayjs(`${today.year() - 1}-06-30`);

        switch (filterValue) {
            case 'today':
                return {
                    startDate: today.format('DD/MM/YYYY'),
                    endDate: '',
                };

            case 'yesterday':
                return {
                    startDate: yesterday.format('DD/MM/YYYY'),
                    endDate: '',
                };

            case 'week':
                return {
                    startDate: today.startOf('week').format('DD/MM/YYYY'), // 本周一
                    endDate: today.format('DD/MM/YYYY'),
                };

            case 'month':
                return {
                    startDate: today.startOf('month').format('DD/MM/YYYY'), // 本月1号
                    endDate: today.format('DD/MM/YYYY'),
                };

            case 'financial':
                if (today.month() >= 6) {
                    // 7月或之后（月份从0开始）
                    financialYearStart = dayjs(`${currentYear}-07-01`);
                } else {
                    financialYearStart = dayjs(`${currentYear - 1}-07-01`);
                }

                return {
                    startDate: financialYearStart.format('DD/MM/YYYY'),
                    endDate: today.format('DD/MM/YYYY'),
                };

            case 'prev':
                return {
                    startDate: prevFinancialYearStart.format('DD/MM/YYYY'),
                    endDate: prevFinancialYearEnd.format('DD/MM/YYYY'),
                };

            default:
                return {
                    startDate: today.format('DD/MM/YYYY'),
                    endDate: today.format('DD/MM/YYYY'),
                };
        }
    };
    const exportFileName = useMemo(() => {
        const { startTime, endTime } = currentSelect;
        if (startTime && endTime) {
            return `Dashboard data (${startTime} - ${endTime}).csv`;
        }
        if (startTime) {
            return `Dashboard data ${startTime}.csv`;
        }
        return 'Dashboard data.csv';
    }, [currentSelect]);
    const { downloadCSV } = useDownLoadCSV(exportFileName);
    const handleExportCSV = async () => {
        const res = await api.dashboardExport(currentSelect);
        const data = res.data.map(item => {
            const {
                date,
                totalActualStripeTax,
                totalCustomerFees,
                totalFaastaaRevenue,
                totalMarketplaceMatched,
                totalMarketplaceValueCompleted,
                totalNumberOfCustomers,
                totalNumberOfTaskers,
                totalTaskerFees,
            } = item;
            return {
                Date: format.customFormat(date, 'D/M/YYYY'),
                'No of new Customers': totalNumberOfCustomers || 0,
                'No of new Taskers': totalNumberOfTaskers || 0,
                'Marketplace matched': `$${totalMarketplaceMatched || '0'}`,
                'Marketplace value completed': `$${totalMarketplaceValueCompleted || '0'}`,
                'Faastaa revenue': `$${totalFaastaaRevenue || '0'}`,
                'Tasker Fees': `$${totalTaskerFees || '0'}`,
                'Customer Fees': `$${totalCustomerFees || '0'}`,
                'Payment Fees': `$${totalActualStripeTax || '0'}`,
            };
        });
        data.unshift({
            Date: 'Date',
            'No of new Customers': 'No of new Customers',
            'No of new Taskers': 'No of new Taskers',
            'Marketplace matched': 'Marketplace matched',
            'Marketplace value completed': 'Marketplace value completed',
            'Faastaa revenue': 'Faastaa revenue',
            'Tasker Fees': 'Tasker Fees',
            'Customer Fees': 'Customer Fees',
            'Payment Fees': 'Payment Fees',
        });
        downloadCSV(data);
    };
    return (
        <>
            <div className="page-main dashboard-main">
                <div className="page-title">Dashboard</div>
                <div className="filter-event flex-between">
                    <div className="cutoma-ant-selection-item">
                        {calendarIcon}
                        {currentSelect.dateType === 'all' ? 'All Data' : currentSelect.startTime}
                        {currentSelect.endTime && ` - ${currentSelect.endTime}`}
                    </div>
                    <Select
                        style={{ zIndex: rangePickerVisible ? 1 : 9 }}
                        defaultValue="all"
                        placeholder="dd/mm/yyyy - dd/mm/yyyy"
                        suffixIcon={<></>}
                        options={filterTypes}
                        onSelect={v => {
                            if (v === 'custom') {
                                setRangePickerVisible(true);
                            } else if (v === 'all') {
                                setSelectValue({ dateType: v, startTime: '', endTime: '' });
                            } else {
                                const { startDate, endDate } = getDateRangeByFilter(v);
                                setSelectValue({
                                    dateType: v,
                                    startTime: startDate,
                                    endTime: endDate,
                                });
                            }
                        }}
                    />
                    <RangePicker
                        style={{ zIndex: rangePickerVisible ? 9 : 1 }}
                        open={rangePickerVisible}
                        onChange={(dates, dateStrings: [string, string]) => {
                            const { startDate, endDate } = getDateRangeByFilter(dateStrings[0], dateStrings[1]);
                            setSelectValue({
                                dateType: 'custom',
                                startTime: startDate,
                                endTime: endDate,
                            });
                            setRangePickerVisible(false);
                        }}
                        disabledDate={disabledDate}
                        onOpenChange={setRangePickerVisible}
                    />
                    <Button type="primary" onClick={handleExportCSV}>
                        Export
                    </Button>
                </div>
                {loading.page ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : (
                    <div className="dashaboard-data">
                        <div className="line-1">
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">
                                    No of active Customers <span className="online-text">online</span>
                                </div>
                                <div className="statistics-num flex-center">{state.nowOfActiveCustomersOnline}</div>
                            </div>
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">
                                    No of active Taskers <span className="online-text">online</span>
                                </div>
                                <div className="statistics-num flex-center">{state.nowOfActiveTaskersOnline}</div>
                            </div>
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">Total number of Customers</div>
                                <div className="statistics-num flex-center">{state.totalOfCustomers}</div>
                            </div>
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">Total number of Taskers</div>
                                <div className="statistics-num flex-center">{state.totalOfTaskers}</div>
                            </div>
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">Total number of Business Accounts</div>
                                <div className="statistics-num flex-center">{state.totalOfBusinessAccounts || '0'}</div>
                            </div>
                        </div>
                        <div className="line-2">
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">TMV posted total, in-progress</div>
                                <div className="statistics-num flex-center">
                                    ${state.totalInProgress ? publicMethod.formatPrice(state.totalInProgress) : '0.00'}
                                </div>
                            </div>
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">Total marketplace matched</div>
                                <div className="statistics-num flex-center">
                                    $
                                    {state.totalMarketplaceMatched
                                        ? publicMethod.formatPrice(state.totalMarketplaceMatched)
                                        : '0.00'}
                                </div>
                            </div>
                            <div className="dashboard-item">
                                <div className="statistics-tip flex-center">Total marketplace value completed</div>
                                <div className="statistics-num flex-center">
                                    $
                                    {state.totalMarketplaceCompleted
                                        ? publicMethod.formatPrice(state.totalMarketplaceCompleted)
                                        : '0.00'}
                                </div>
                            </div>
                        </div>
                        <div className="dashboard-item flex-center" style={{ marginTop: 24 }}>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div className="statistics-tip flex-center">Total Faastaa revenue</div>
                                <div className="statistics-num flex-center">
                                    ${state.totalEarnings ? publicMethod.formatPrice(state.totalEarnings) : '0.00'}
                                </div>
                            </div>
                            <span className="line-cneter"></span>
                            <div style={{ flex: 1 }}>
                                <div className="statistics-tip flex-center" style={{ justifyContent: 'flex-start' }}>
                                    Total Tasker Fees
                                </div>
                                <div className="min-price flex-center" style={{ justifyContent: 'flex-start' }}>
                                    ${state.taskerFees ? publicMethod.formatPrice(state.taskerFees) : '0.00'}
                                </div>
                                <div
                                    className="statistics-tip flex-center"
                                    style={{ marginTop: 12, justifyContent: 'flex-start' }}
                                >
                                    Total Customer Fees
                                </div>
                                <div className="min-price flex-center" style={{ justifyContent: 'flex-start' }}>
                                    ${state.faastaaFees ? publicMethod.formatPrice(state.faastaaFees) : '0.00'}
                                </div>
                            </div>
                        </div>
                        {/* Chart  */}
                        <div className="chart-main">
                            <Chart
                                data={chartData.totalMarketValue}
                                title="Total Market Value"
                                text="Value"
                                loading={loading.totalMarketValue}
                                dateType={currentSelect.dateType}
                            />
                            <Chart
                                data={chartData.onlineUser}
                                title="Trend of online Users"
                                loading={loading.onlineUser}
                                dateType={currentSelect.dateType}
                            />
                            <Chart
                                data={chartData.newUser}
                                title="Trend of new Users"
                                loading={loading.newUser}
                                dateType={currentSelect.dateType}
                            />
                            <Chart
                                data={chartData.totalUser}
                                title="Trend of total Users"
                                loading={loading.totalUser}
                                dateType={currentSelect.dateType}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Dashboard;
